import { useState, useMemo } from "react";
import { X, Save, Loader2, TrendingDown, TrendingUp, Target } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n.jsx";

export default function GoalWeightModal({ onClose, currentTarget, currentWeight, onSaved }) {
  const { lang } = useI18n();
  const en = lang === "en";
  const [target, setTarget] = useState(currentTarget || "");
  const [saving, setSaving] = useState(false);

  const t = Number(target);
  const c = Number(currentWeight);
  const inferredGoal = useMemo(() => {
    if (!t || !c) return null;
    const delta = t - c;
    if (delta < -0.5) return { l: en ? "Weight loss" : "خسارة الوزن", color: "text-emerald-600", icon: TrendingDown };
    if (delta > 0.5) return { l: en ? "Weight gain" : "زيادة الوزن", color: "text-sky-600", icon: TrendingUp };
    return { l: en ? "Weight maintenance" : "الحفاظ على الوزن", color: "text-neutral-600", icon: Target };
  }, [t, c, en]);

  const save = async () => {
    if (!t || t < 20 || t > 500) return toast.error(en ? "Enter a valid target weight" : "أدخل وزناً هدفاً صحيحاً");
    setSaving(true);
    try {
      const { data } = await api.patch("/user/target-weight", { target_weight_kg: t });
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (en ? "Couldn't save" : "تعذر الحفظ"));
    } finally {
      setSaving(false);
    }
  };

  const Icon = inferredGoal?.icon || Target;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose} dir={en ? "ltr" : "rtl"}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-3xl overflow-hidden" data-testid="goal-weight-modal">
        <div className="p-4 border-b border-neutral-100 flex items-center justify-between">
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center" data-testid="goal-modal-close">
            <X className="w-4 h-4" />
          </button>
          <div className="font-bold">{en ? "Target weight" : "الوزن المرغوب"}</div>
        </div>
        <div className="p-5 space-y-4">
          <div className={`bg-brand-50/60 rounded-2xl p-3 ${en ? "text-left" : "text-right"}`}>
            <div className="text-[11px] text-neutral-500">{en ? "Your current weight" : "وزنك الحالي"}</div>
            <div className="text-lg font-black num text-neutral-900">{c ? c.toFixed(1) : "—"} <span className="text-xs text-neutral-400">kg</span></div>
          </div>
          <div>
            <div className={`text-xs text-neutral-500 mb-1.5 font-semibold ${en ? "text-left" : "text-right"}`}>
              {en ? "Target weight (kg)" : "الوزن الهدف (كجم)"}
            </div>
            <input
              type="number" step="0.1" min="20" max="500"
              value={target} onChange={(e) => setTarget(e.target.value)}
              className={`w-full bg-white shadow-card rounded-2xl px-4 py-3 text-2xl font-black num focus:outline-none ${en ? "text-left" : "text-right"}`}
              data-testid="goal-weight-input"
              autoFocus
            />
          </div>
          {inferredGoal && (
            <div className={`flex items-center gap-1.5 text-xs font-semibold ${inferredGoal.color} ${en ? "justify-start" : "justify-end"}`} data-testid="goal-inferred">
              <span>{inferredGoal.l}</span>
              <Icon className="w-4 h-4" />
            </div>
          )}
          <div className={`text-[11px] text-neutral-500 leading-relaxed bg-neutral-50 rounded-2xl p-3 ${en ? "text-left" : "text-right"}`}>
            {en
              ? "Your daily calories, protein, carbs and fat will be automatically recalculated based on your new target weight and current weight (auto mode only)."
              : "سيتم إعادة حساب السعرات اليومية والبروتين والكربوهيدرات والدهون تلقائياً وفق الوزن الهدف الجديد ووزنك الحالي (الوضع التلقائي فقط)."}
          </div>
        </div>
        <div className="p-4 border-t border-neutral-100">
          <button onClick={save} disabled={saving}
            className="w-full orange-gradient text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-1 disabled:opacity-60"
            data-testid="goal-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> {en ? "Save target" : "حفظ الهدف"}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
